import deleteUserRoleApi from '@/api/delete/user-role/delete-user-role';
import getUserRolesApi from '@/api/get/user-management/get-user-roles';
import Alert from '@/components/alert/alert-component';
import { PrimaryButton } from '@/components/button/button-component';
import ConfirmationModal from '@/components/confirmation-modal/confirmation-modal';
import TabPortal from '@/components/page-tabs/page-tab-portal-component';
import PageWrapper from '@/components/page-wrapper/page-wrapper-component';
import PermissionWrapper from '@/components/permission-wrapper/permission-wrapper-component';
import StatusIndicator from '@/components/status-indicator/status-indicator-component';
import ViewEditDelete from '@/components/table/components/view-edit-delete/view-edit-delete';
import Table from '@/components/table/table';
import { useLazyQuery, useMutation } from '@/hooks/use-api';
import { useCallback, useState, useEffect } from 'react';
import { FiEdit } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

const renderStatus = ({ row }) => (
  <StatusIndicator status={row.original.status.toLowerCase()} />
);

/**
 * @returns {JSX.Element}
 */
const UserRoleAll = () => {
  const tableId = 'user-role-list';
  const [fetchingPresets, setFetchingPresets] = useState(true);
  const [defaultVariables, setDefaultVariables] = useState({});
  const [defaultSort, setDefaultSort] = useState('created_at:DESC');
  const navigate = useNavigate();
  const { data, error, loading, refetch, count } =
    useLazyQuery(getUserRolesApi);

  const [deleteUserRole, { loading: deleteUserRoleLoading }] =
    useMutation(deleteUserRoleApi);

  const getTablePresets = useCallback(() => {
    const tablePreferences = localStorage.getItem('tablePreferences');
    if (tablePreferences) {
      const tableConfigs = JSON.parse(tablePreferences)[tableId];

      if (tableConfigs) {
        const { query } = tableConfigs;

        if (query.length > 0) {
          // Get cached search value and apply it
          const searchQuery = query?.filter((q) => q.includes('search='));
          if (searchQuery?.length > 0) {
            const tempSearch = searchQuery[0].replace('search=', '');
            setDefaultVariables({ ...defaultVariables, search: tempSearch });
          }

          // Get cached sort value and apply it
          const sortQuery = query?.filter((q) => q.includes('sort='));
          if (sortQuery?.length > 0) {
            const tempSort = sortQuery[0].replace('sort=', '');
            setDefaultSort(tempSort);
          }

          // Get cached page size and apply it
          const pageSizeQuery = query?.filter((q) => q.includes('limit='));
          if (pageSizeQuery?.length > 0) {
            const tempPageSize = parseInt(
              pageSizeQuery[0].replace('limit=', ''),
              10,
            );
            setDefaultVariables({ ...defaultVariables, limit: tempPageSize });
          }
        }
      }
    }
    setFetchingPresets(false);

    // This is disabled because we don't want to re-run this function when the defaultVariables changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // USE EFFECTS
  useEffect(() => {
    getTablePresets();
  }, [getTablePresets]);

  const launchDeleteConfirmation = useCallback(
    (userData) => {
      ConfirmationModal({
        title: 'Delete User Role',
        text: `Are you sure you want to delete ${userData.values.name}?`,
        onConfirm: () =>
          deleteUserRole(userData?.original?.id, userData).then(() => {
            refetch().then(() => {
              Alert({
                icon: 'success',
                text: 'User deleted successfully',
              });
            });
          }),
      });
    },
    [deleteUserRole, refetch],
  );

  const handleEdit = useCallback(
    (row) => {
      const userRoleDetails = row.original;
      navigate(`edit/${userRoleDetails.id}`);
    },
    [navigate],
  );

  const handleDelete = useCallback(
    (row) => {
      launchDeleteConfirmation(row);
    },
    [launchDeleteConfirmation],
  );

  const handleView = useCallback(
    (row) => {
      const userRoleDetails = row.original;
      navigate(`view/${userRoleDetails.id}`);
    },
    [navigate],
  );

  const Actions = useCallback(
    ({ row }) => (
      <ViewEditDelete
        feature="userRole"
        onEdit={() => handleEdit(row)}
        onView={() => handleView(row)}
        onDelete={() => handleDelete(row)}
        onDuplicate={null}
      />
    ),
    [handleDelete, handleEdit, handleView],
  );

  const UserRolesColumns = [
    {
      Header: 'USER ROLE NAME',
      id: 'name',
      accessor: 'name',
      search: true,
    },
    {
      Header: 'TOTAL USER',
      id: 'total_user',
      accessor: 'total_user',
      search: true,
    },
    {
      Header: 'DESCRIPTION',
      id: 'description',
      accessor: 'description',
      search: true,
    },
    {
      Header: 'STATUS',
      id: 'status',
      accessor: 'status',
      search: true,
      Cell: renderStatus,
    },
    {
      Header: 'ACTIONS',
      id: 'actions',
      Cell: (props) => Actions(props),
    },
  ];

  return (
    <PageWrapper>
      <TabPortal>
        <PermissionWrapper permissions={['userRole.create']}>
          <PrimaryButton
            title="New Role"
            onClick={() => navigate('new')}
            buttonHeight="40px"
            icon={<FiEdit />}
          />
        </PermissionWrapper>
      </TabPortal>
      <Table
        feature="userRole"
        onRowEdit={(row) => handleEdit(row)}
        onRowDelete={(row) => handleDelete(row)}
        onRowView={(row) => handleView(row)}
        columns={UserRolesColumns}
        data={data ?? []}
        tableOptions={['search', 'columns']}
        id={tableId}
        defaultSort={defaultSort}
        defaultVariables={defaultVariables}
        dataCount={count ?? 0}
        downloadAccessor={data?.length || []}
        error={error}
        loading={loading || deleteUserRoleLoading || fetchingPresets}
        filters={[]}
        refetch={refetch}
      />
    </PageWrapper>
  );
};

export default UserRoleAll;
